import { z } from "zod";
import { RemiPackage } from "@remi/error/remi-error/errorTypes/remi-package";
import { RemiError } from "@remi/error/remi-error/RemiError";
import { ErrorSeverity } from "@remi/error/sentry/severity";

const environmentValues = ["development", "sandbox", "preview", "production"] as const;
const EnvironmentSchema = z.enum(environmentValues);

const EDEN_STUB_READY_AFTER_DEFAULT_MS = 25_000;

export const EdenStubForceSchema = z.enum(["stale", "expired", "payload_invalid"]);
const HTTP_ORIGIN_PROTOCOLS: ReadonlySet<string> = new Set(["http:", "https:"]);
const MIN_EDEN_SUFFIX_LABELS = 3;

const splitEnvList = (value: string | undefined): string[] =>
  (value ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

const HttpOriginListSchema = z
  .string()
  .optional()
  .transform((value, ctx) =>
    splitEnvList(value).flatMap((entry) => {
      const origin = URL.canParse(entry) ? new URL(entry) : null;
      if (origin === null || !HTTP_ORIGIN_PROTOCOLS.has(origin.protocol) || origin.origin === "null") {
        ctx.addIssue({ code: "custom", message: `Not an http(s) origin: ${entry}` });
        return [];
      }
      return [origin.origin];
    }),
  );

const EdenAllowedOriginSuffixesSchema = z
  .string()
  .optional()
  .transform((value, ctx) =>
    splitEnvList(value).filter((entry) => {
      const labels = entry.split(".");
      const isHostSuffix = labels.length >= MIN_EDEN_SUFFIX_LABELS && labels.every((label) => label.length > 0);
      if (!isHostSuffix) {
        ctx.addIssue({ code: "custom", message: `Not a host suffix: ${entry}` });
      }
      return isHostSuffix;
    }),
  );

const RemiEnvSchema = {
  ABLY_API_KEY: z.string(),
  ANTHROPIC_API_KEY: z.string(),
  AIRBYTE_PREVIEW_CLIENT_ID: z.string(),
  AIRBYTE_PREVIEW_CLIENT_SECRET: z.string(),
  AIRBYTE_PREVIEW_URL: z.string(),
  AIRTABLE_API_KEY: z.string().optional(),
  APP_URL: z.url(),
  MOBILE_APP_SCHEME: z.string(),
  AWS_CLOUD_FRONT_ACCESS_KEY: z.string(),
  AWS_CLOUD_FRONT_ACCESS_KEY_ID: z.string(),
  AWS_CLOUD_FRONT_URL: z.string(),
  AWS_REGION: z.string(),
  AWS_S3_BUCKET_NAME: z.string(),
  AWS_S3_STATIC_BUCKET_NAME: z.string(),
  AWS_S3_EMAIL_OBJECT_BUCKET_NAME: z.string().optional(),
  AXIOM_DATASET: z.string(),
  AXIOM_QUERY_TOKEN: z.string().optional(),
  AXIOM_TOKEN: z.string(),
  CARTESIA_API_KEY: z.string().optional(),
  CLERK_SECRET_KEY: z.string(),
  CLERK_SECRET_KEY_DEVELOPMENT: z.string(),
  CLERK_JWT_KEY: z.string().optional(),
  CLOUDFLARE_ACCOUNT_ID: z.string().optional(),
  CLOUDFLARE_AI_API_TOKEN: z.string().optional(),
  DATABASE_URL: z.url(),
  EDEN_ALLOWED_ORIGINS: HttpOriginListSchema,
  EDEN_ALLOWED_ORIGIN_SUFFIXES: EdenAllowedOriginSuffixesSchema,
  EDEN_SUGGESTION_STUBS: z.string().optional(),
  EDEN_AGENT_ACTION_SUGGESTIONS: z.string().optional(),
  EDEN_STUB_FORCE: EdenStubForceSchema.optional().catch(undefined),
  EDEN_STUB_READY_AFTER_MS: z.coerce.number().int().min(0).optional().prefault(EDEN_STUB_READY_AFTER_DEFAULT_MS),
  DATABASE_URL_READ_ONLY: z.url().optional(),
  DOPPLER_PROJECT: z.string().optional(),
  DOPPLER_CONFIG: EnvironmentSchema.optional().catch(undefined),
  DOPPLER_SDK_TOKEN: z.string(),
  DOMBOL_META_CONVERSIONS_API_KEY: z.string().optional(),
  DOMBOL_META_DATASET_ID: z.string().optional(),
  DOMBOL_META_APP_SECRET: z.string().optional(),
  DOMBOL_META_PAGE_ACCESS_TOKEN: z.string().optional(),
  DOMBOL_META_WEBHOOK_VERIFY_TOKEN: z.string().optional(),
  DOMBOL_META_LEADGEN_FIXTURE: z.string().optional(),
  E2E: z.string().optional(),
  EAGLEVIEW_CLIENT_ID: z.string(),
  EAGLEVIEW_CLIENT_SECRET: z.string(),
  EAGLEVIEW_URL: z.string(),
  EMMA_HANDOFF_SECRET: z.string().optional(),
  EMMA_HANDOFF_URL: z.url({ protocol: /^https$/ }).optional(),
  EQHO_API_KEY: z.string(),
  FC_GIT_COMMIT_SHA: z.string().optional(),
  FC_PREVIEW_DATABASE_URL_PARAMETER_NAME: z.string(),
  FC_URL: z.url().optional(),
  FIGMA_ACCESS_TOKEN: z.string(),
  FINGERPRINT_SECRET_API_KEY: z.string().optional(),
  GIT_COMMIT_SHA: z.string().optional(),
  GCP_SERVICE_ACCOUNT_PRIVATE_KEY: z.string(),
  GMAIL_SERVICE_ACCOUNT_CLIENT_EMAIL: z.string(),
  GMAIL_SERVICE_ACCOUNT_PRIVATE_KEY: z.string(),
  GOODLEAP_USERNAME: z.string(),
  GOODLEAP_PASSWORD: z.string(),
  GOODLEAP_BASE_URL: z.string(),
  GOODLEAP_PORTAL_BASE_URL: z.string(),
  GOODLEAP_WEBHOOK_AUTHORIZATION_TOKEN: z.string(),
  GOOGLE_CALENDAR_SERVICE_ACCOUNT_CLIENT_EMAIL: z.string(),
  GOOGLE_CALENDAR_SERVICE_ACCOUNT_PRIVATE_KEY: z.string(),
  GOOGLE_CALENDAR_DELEGATE_EMAIL: z.string(),
  GOOGLE_SHEETS_SERVICE_ACCOUNT_CLIENT_EMAIL: z.string(),
  GOOGLE_SHEETS_SERVICE_ACCOUNT_PRIVATE_KEY: z.string(),
  GOOGLE_SHEETS_PARTNER_PORTAL_SPREADSHEET_ID: z.string(),
  GROOVE_API_KEY: z.string().optional(),
  GROOVE_STALE_AWAITING_DAYS: z.coerce.number().int().positive().optional(),
  // Optional so environments without it (or older configs) degrade gracefully: the
  // best-effort translation service no-ops rather than failing boot. See GoogleTranslateApi.
  GOOGLE_TRANSLATE_API_KEY: z.string().optional(),
  GOOGLE_MAPS_API_KEY: z.string(),
  GOOGLE_TIMEZONE_API_KEY: z.string(),
  GOOGLE_ADS_SERVICE_ACCOUNT_CLIENT_EMAIL: z.string().optional(),
  GOOGLE_ADS_SERVICE_ACCOUNT_PRIVATE_KEY: z.string().optional(),
  GOOGLE_ADS_LOGIN_CUSTOMER_ID: z.string().optional(),
  GOOGLE_ADS_LSA_CUSTOMER_ID: z.string().optional(),
  GOOGLE_AI_API_KEY: z.string(),
  GROQ_API_KEY: z.string(),
  HEADLESS: z.enum(["true", "false"]).optional(),
  PLATEAU_TILE_BASE_URL: z.url().optional(),
  PUPPETEER_RECORD: z.enum(["true", "false"]).optional(),
  PUPPETEER_RECORD_DIR: z.string(),
  HELICONE_API_KEY: z.string(),
  HOST_NAME: z.string().optional(),
  HUBSPOT_API_KEY: z.string(),
  HUBSPOT_INTEGRATIONS_URL: z.string(),
  HUBSPOT_DEV_ENABLED: z.coerce.boolean().optional().prefault(false),
  INSPECTIFY_URL: z.url(),
  INSPECTIFY_API_KEY: z.string(),
  INSPECTIFY_WEBHOOK_SECRET: z.string().optional(),
  INSPECTIFY_CANCEL_WEBHOOK_SECRET: z.string().optional(),
  INSPECTIFY_WEBHOOK_API_KEY: z.string().optional(),
  INSPECTIFY_AUTOMATIONS_API_KEY: z.string().optional(),
  INSPECTIFY_TEMPLATE_EXTERIOR: z.string().optional(),
  INSURANCE_CLAIM_INTRO_BASE_URL: z.url().optional(),
  ITEL_DOWNLOAD_JWT_SECRET: z.string().optional(),
  LEAD_DIAL_SCENARIO_NUMBERS: z.string().optional(),
  LEAD_DIAL_TEST_API_KEY: z.string().optional(),
  LEHI_FIELD_SALES_CALENDAR_ID: z.string(),
  LIDAR_SERVER_URL: z.url(),
  LIDAR_SERVER_USERNAME: z.string(),
  LIDAR_SERVER_PASSWORD: z.string(),
  LINEAR_API_KEY: z.string(),
  LINEAR_ISSUE_CHANGE_WEBHOOK_SECRET: z.string(),
  LOCAL_FEATURE_FLAG_OVERRIDES: z.string().optional(),
  LOCAL_SCRIPT: z.enum(["true", "false"]).optional(),
  KNOCK_SECRET_API_KEY: z.string(),
  KNOCK_WEBHOOK_SIGNING_KEY: z.string(),
  MARKETING_SITE_ORIGINS: HttpOriginListSchema,
  MOBILE_CONTRACTOR_MINIMUM_VERSION: z.string(),
  NEARMAP_API_KEY: z.string(),
  NODE_ENV: z.enum(["development", "production", "test"]),
  NOTIFY_ANNOUNCEMENTS_SALES_URL: z.string(),
  NOTIFY_DEAL_DESK_URL: z.string(),
  NOTIFY_ENG_SUPPORT_URL: z.string(),
  NOTIFY_HO_FEEDBACK_URL: z.string(),
  NOTIFY_REGIONAL_MANAGER_URL: z.string(),
  NOTIFY_SUPPORT_URL: z.string(),
  NOTIFY_SX_ENG_SUPPORT_URL: z.string(),
  NOTIFY_TEAM_REGIONAL_MANAGEMENT_URL: z.string(),
  NOTIFY_TEAM_DATABASE_URL: z.string(),
  NOTIFY_TEAM_FINANCE_URL: z.string(),
  NOTIFY_TEAM_QUOTING_URL: z.string(),
  NOTIFY_PROD_SCRIPTS: z.string(),
  NOTIFY_INSURANCE_AGREEMENT_URL: z.string(),
  NOTIFY_INSURANCE_INTROS_URL: z.string().optional(),
  NOTIFY_INSURANCE_SOW_CHANGES_URL: z.string().optional(),
  NOTIFY_LEAD_ALERTS_URL: z.string().optional(),
  NOTIFY_TEAM_MARKETING_ROADMAP_URL: z.string().optional(),
  NOTIFY_TEAM_FP_AND_A_URL: z.string().optional(),
  NOTIFY_SITE_VISIT_ASSIGNED_URL: z.string().optional(),
  NOTIFY_CHANGE_ORDER_URL: z.string(),
  NOTIFY_APPEASEMENT_REVIEW_URL: z.string().optional(),
  NOTIFY_CONTRACTOR_COMPLIANCE_URL: z.string(),
  OPENAI_API_KEY: z.string(),
  OUTREACH_INGEST_LOOKBACK_MINUTES: z.coerce.number().int().positive().optional().prefault(30),
  OUTREACH_SEND_ALLOWLIST: z.string().optional(),
  OUTREACH_SEND_MODE: z.enum(["dry-run", "live"]).default("dry-run"),
  OPENPHONE_API_KEY: z.string(),
  OPENPHONE_CALLS_WEBHOOK_SECRET: z.string(),
  OPENPHONE_CALL_RECORDINGS_WEBHOOK_SECRET: z.string(),
  OPENPHONE_CALL_RECORDINGS_PII_WEBHOOK_SECRET: z.string(),
  OPENPHONE_CALL_SUMMARIES_WEBHOOK_SECRET: z.string(),
  OPENPHONE_CALL_TRANSCRIPTS_WEBHOOK_SECRET: z.string(),
  OPENPHONE_MESSAGES_WEBHOOK_SECRET: z.string(),
  OPEN_PHONE_PHONE_NUMBER: z.string(),
  OPENPHONE_VIBE_CHECK_SMS_LOOKBACK_MINUTES: z.coerce.number().optional().prefault(60),
  OPENPHONE_VIBE_CHECK_SMS_WEEKEND_LOOKBACK_MINUTES: z.coerce.number().optional().prefault(120),
  OPENPHONE_VIBE_CHECK_CALLS_LOOKBACK_MINUTES: z.coerce.number().optional().prefault(60),
  OPENPHONE_VIBE_CHECK_CALLS_WEEKEND_LOOKBACK_MINUTES: z.coerce.number().optional().prefault(120),
  DROPBOX_SIGN_API_KEY: z.string(),
  DROPBOX_SIGN_CLIENT_ID: z.string(),
  DROPBOX_SIGN_WEBHOOK_SECRET: z.string(),
  DROPBOX_SIGN_MSA_TEMPLATE_ID: z.string(),
  DROPBOX_SIGN_SAFETY_POLICY_TEMPLATE_ID: z.string(),
  DROPBOX_SIGN_ACH_TEMPLATE_ID: z.string(),
  PANDADOC_API_KEY: z.string(),
  PANDADOC_SHARED_WEBHOOK_KEY: z.string(),
  PAY_KEEPER_DEFAULT_SCHEDULE_ID: z.string(),
  PAY_KEEPER_ORGANIZATION_ID: z.string(),
  PAY_KEEPER_PASSWORD: z.string(),
  PAY_KEEPER_R1_REINSTALL_SCHEDULE_ID: z.string(),
  PAY_KEEPER_R1_REMOVAL_SCHEDULE_ID: z.string(),
  PAY_KEEPER_R2_SCHEDULE_ID: z.string(),
  PAY_KEEPER_R2_V2_SCHEDULE_ID: z.string(),
  PAY_KEEPER_R3_SCHEDULE_ID: z.string(),
  PAY_KEEPER_R3_V2_SCHEDULE_ID: z.string(),
  PAY_KEEPER_ROOF_REINSTALL_SCHEDULE_ID: z.string(),
  PAY_KEEPER_ROOF_WORK_SCHEDULE_ID: z.string(),
  PAY_KEEPER_V2_BASE_URL: z.url(),
  PAY_KEEPER_WEBHOOK_PASSWORD: z.string(),
  PAY_KEEPER_WEBHOOK_SIGNING_SECRET: z.string(),
  PDF_RENDERER_PASSWORD: z.string(),
  PDF_RENDERER_URL: z.string(),
  PDF_RENDERER_USERNAME: z.string(),
  PLANETSCALE_SERVICE_TOKEN: z.string(),
  PLANETSCALE_SERVICE_TOKEN_ID: z.string(),
  PLANETSCALE_WEBHOOK_SECRET: z.string(),
  PLANET_SCALE_CLEANUP_TOKEN: z.string().optional(),
  PLANET_SCALE_CLEANUP_TOKEN_ID: z.string().optional(),
  PORT: z.string().optional(),
  PORTAL_AUTOMATIONS_AWS_ACCESS_KEY_ID: z.string(),
  PORTAL_AUTOMATIONS_AWS_SECRET_ACCESS_KEY: z.string(),
  PORTAL_URL: z.url(),
  POSTHOG_API_KEY: z.string(),
  POST_PTO_PORTAL_AUTOMATIONS_FAST_SQS_QUEUE_URL: z.url(),
  POST_PTO_PORTAL_AUTOMATIONS_SQS_QUEUE_URL: z.url(),
  PRE_PTO_PORTAL_AUTOMATIONS_FAST_SQS_QUEUE_URL: z.url(),
  PRE_PTO_PORTAL_AUTOMATIONS_SQS_QUEUE_URL: z.url(),
  PROJECT_COUNT: z.string().optional(),
  // z.enum, not z.coerce.boolean: coerce makes the string "false" truthy, which would silently
  // disable the reconciler's mass-assign abort guard. Read site gates on === "true".
  RECONCILE_AM_ASSIGN_LIMIT_OVERRIDE: z.enum(["true", "false"]).optional(),
  RECONCILE_CONVERSATION_ASSIGNEES_LOOKBACK_HOURS: z.string().optional(),
  RECONCILE_CONVERSATION_ASSIGNEES_MAX_WORKSITE_GROUPS: z.string().optional(),
  QB_API_URL: z.string(),
  QB_CLIENT_ID: z.string(),
  QB_CLIENT_SECRET: z.string(),
  QB_COMPANY_ID: z.string(),
  QB_ENV: z.string(),
  QB_WEBHOOK_SECRET: z.string(),
  QSTASH_CURRENT_SIGNING_KEY: z.string(),
  QSTASH_NEXT_SIGNING_KEY: z.string(),
  QSTASH_TOKEN: z.string(),
  QSTASH_URL: z.url(),
  QXO_ACCOUNT_ID: z.string(),
  QXO_API_BASE_URL: z.url(),
  QXO_API_SITE_ID: z.string().optional(),
  QXO_PASSWORD: z.string(),
  QXO_USERNAME: z.string(),
  UPSTASH_REDIS_REST_TOKEN: z.string(),
  UPSTASH_REDIS_REST_URL: z.url(),
  REMI_API_KEY: z.string(),
  REMI_HUBSPOT_INTEGRATIONS_BASIC_AUTH: z.string(),
  RESEND_API_KEY: z.string(),
  OTP_HMAC_SECRET: z.string(),
  R_AND_R_SALESFORCE_PASSWORD: z.string(),
  R_AND_R_SALESFORCE_USERNAME: z.string(),
  S3_AWS_ACCESS_KEY_ID: z.string(),
  S3_AWS_PUBLIC_BUCKET_NAME: z.string(),
  S3_AWS_SECRET_ACCESS_KEY: z.string(),
  S3_SCRIPTS_BUCKET_NAME: z.string(),
  SALESFORCE_PASSWORD: z.string(),
  SALESFORCE_USERNAME: z.string(),
  SECRETS_MANAGER_AWS_ACCESS_KEY_ID: z.string(),
  SECRETS_MANAGER_AWS_SECRET_ACCESS_KEY: z.string(),
  SERVICE_NAME: z.string(),
  SLACK_BOT_TOKEN: z.string(),
  SMARTY_AUTH_ID: z.string(),
  SMARTY_AUTH_TOKEN: z.string(),
  AUTOPSY_SNOWFLAKE_ACCOUNT_ID: z.string(),
  AUTOPSY_SNOWFLAKE_PRIVATE_KEY: z.string(),
  AUTOPSY_SNOWFLAKE_URL: z.string(),
  AUTOPSY_SNOWFLAKE_USER: z.string(),
  SNOWFLAKE_PRIVATE_KEY: z.string(),
  SRS_API_BASE_URL: z.url(),
  SRS_CLIENT_ID: z.string(),
  SRS_CLIENT_SECRET: z.string(),
  SUNLIGHT_BASE_URL: z.string(),
  SUNLIGHT_BOOMI_EMAIL: z.string(),
  SUNLIGHT_BOOMI_TOKEN: z.string(),
  SUNLIGHT_EMAIL: z.string(),
  SUNLIGHT_PASSWORD: z.string(),
  SUNLIGHT_PORTAL_BASE_URL: z.string(),
  SUNLIGHT_HOME_API_WEBHOOK_TOKEN: z.string(),
  SUNLIGHT_WEBHOOK_PASSWORD: z.string(),
  SUNRUN_AUTOMATIONS_API_KEY: z.string(),
  SUNRUN_AUTOMATIONS_FAST_SQS_QUEUE_URL: z.string(),
  SUNRUN_AUTOMATIONS_SALT: z.string(),
  SUNRUN_AUTOMATIONS_SQS_QUEUE_URL: z.string(),
  SUNRUN_AUTOMATIONS_TOKEN: z.string(),
  SUNRUN_AUTOMATIONS_TOKEN_ID: z.string(),
  // Unset is the safety gate: the weekly report still builds, but no partner email is ever sent.
  SUNRUN_FS_BILLING_RECIPIENTS: z.string().optional(),
  SUNRUN_FS_BILLING_SENDER_NAME: z.string().optional(),
  SUNRUN_INTEGRATION_API_KEY: z.string().optional(),
  SUNRUN_INTEGRATION_SERVER_URL: z.url().optional(),
  SUNRUN_INTEGRATION_WEBHOOK_SECRET: z.string().optional(),
  SUNRUN_REPORT_FINALIZE_ENABLED: z.stringbool().default(false),
  SUNRUN_WEBHOOK_SECRET_KEY_RRC: z.string(),
  WESTHILL_AUTOMATIONS_API_KEY: z.string(),
  WESTHILL_AUTOMATIONS_SALT: z.string(),
  WESTHILL_AUTOMATIONS_TOKEN_ID: z.string(),
  WESTHILL_AUTOMATIONS_TOKEN: z.string(),
  WESTHILL_CERTIFICATE_OF_COMPLETION_COMPLETE_INGESTION_WEBHOOK_SECRET: z.string(),
  WESTHILL_CERTIFICATE_OF_COMPLETION_INVOICE_CREATED_INGESTION_WEBHOOK_SECRET: z.string(),
  WESTHILL_HIC_SIGNED_INGESTION_WEBHOOK_SECRET: z.string(),
  WESTHILL_HOMEOWNER_CONTACTED_INGESTION_WEBHOOK_SECRET: z.string(),
  WESTHILL_MESSAGE_INGESTION_WEBHOOK_SECRET: z.string(),
  WESTHILL_NTP_RECEIVED_INGESTION_WEBHOOK_SECRET: z.string(),
  WESTHILL_PROJECT_COMPLETED_INGESTION_WEBHOOK_SECRET: z.string(),
  WESTHILL_PROJECT_SCHEDULED_INGESTION_WEBHOOK_SECRET: z.string(),
  WESTHILL_PROJECT_STARTED_INGESTION_WEBHOOK_SECRET: z.string(),
  WESTHILL_SALE_CLOSED_LOST_INGESTION_WEBHOOK_SECRET: z.string(),
  WESTHILL_SITE_VISIT_COMPLETED_INGESTION_WEBHOOK_SECRET: z.string(),
  WESTHILL_WORK_COMPLETED_INGESTION_WEBHOOK_SECRET: z.string(),
  SUNRUN_WEBHOOK_SECRET_KEY_RRS: z.string(),
  SUNRUN_SOAP_LOGIN_SECRET_ID_RR_SOLUTIONS: z.string(),
  SUNRUN_SOAP_LOGIN_SECRET_ID_RR_CONSTRUCTION: z.string(),
  SUNRUN_FIC_BASE_URL: z.url(),
  SUNRUN_FIC_API_KEY: z.string(),
  TINYBIRD_BASE_URL: z.url().min(1).optional(),
  TINYBIRD_READ_TOKEN: z.string().min(1).optional(),
  TINYBIRD_WRITE_TOKEN: z.string().min(1).optional(),
  TINYBIRD_BASE_URL_DEV: z.url().min(1).optional(),
  TINYBIRD_READ_TOKEN_DEV: z.string().min(1).optional(),
  TINYBIRD_WRITE_TOKEN_DEV: z.string().min(1).optional(),
  TRAINING_SANDBOX_PROVISION_SECRET: z.string().min(32).optional(),
  TRAINING_SANDBOX_URL: z.url().optional(),
  TYPEFORM_WEBHOOK_SIGNING_SECRET: z.string(),
  TYPESAFE_API_KEY: z.string().optional(),
  TWILIO_ACCOUNT_SID: z.string(),
  TWILIO_AUTH_TOKEN: z.string(),
  TWILIO_AUTOMATED_NUMBER: z.string(),
  TWILIO_DIRECT_NUMBER: z.string(),
  TWILIO_MESSAGING_SERVICE_SID: z.string(),
  TWILIO_PROXY_SERVICE_SID: z.string(),
  PORTLESS_NAMESPACE: z.string().optional(),
  VERBOSE_DB_LOGGING: z.string().optional(),
  VERBOSE_TEST_LOGGING: z.string().optional(),
  REMI_AUTOMATIONS_USER: z.string().optional(),
  VERCEL_API_TOKEN: z.string(),
  VERCEL_DEPLOYMENT_ID: z.string().optional(),
  VERCEL_ENV: EnvironmentSchema.optional(),
  VERCEL_GIT_COMMIT_SHA: z.string().optional(),
  VERCEL_REGION: z.string().optional(),
  VERCEL_URL: z.string().optional(),
  VITEST: z.string().optional(),
  WEB_PUSH_VAPID_PRIVATE_KEY: z.string().optional(),
  CORRECTIONS_JEV_SHADOW_OVERRIDE: z.enum(["true", "false"]).optional(),
  SOW_CHANGE_FLOW_OVERRIDE: z.enum(["true", "false"]).optional(),
  CORRECTIONS_REVIEW_TASK_OVERRIDE: z.enum(["true", "false"]).optional(),
  CORRECTIONS_AUTO_CREATE_OVERRIDE: z.enum(["true", "false"]).optional(),
  XACTANALYSIS_WEBHOOK_VERIFICATION_TOKEN: z.string(),
  SYMBILITY_WEBHOOK_VERIFICATION_TOKEN: z.string(),
  XACTANALYSIS_SERVER_URL: z.string(),
  XACTANALYSIS_CLIENT_ID: z.string(),
  XACTANALYSIS_CLIENT_SECRET: z.string(),
  XACTANALYSIS_AMFAM_ESTIMATE_ONLY_CARRIER_ID: z.string().min(1).optional(),
  XACTANALYSIS_AMFAM_FULL_ROOF_CARRIER_ID: z.string().min(1).optional(),
  XACTANALYSIS_USER_ID: z.string(),
  VERISK_USERNAME: z.string(),
  VERISK_PASSWORD: z.string(),
  VERISK_OTP_SECRET: z.string(),
  WEBHOOK_PATH_TOKEN_HANDLE_FILE_UPLOAD: z.string(),
  HANDLE_FILE_UPLOAD_WEBHOOK_BASE_URL: z.url().optional(),
  HANDLE_FILE_UPLOAD_SOURCE_BUCKET: z.string().min(1).optional(),
  WEBHOOK_PATH_TOKEN_SRS_EVENT: z.string(),
  WEBHOOK_PROCESSOR_AWS_ACCESS_KEY_ID: z.string(),
  WEBHOOK_PROCESSOR_AWS_SECRET_ACCESS_KEY: z.string(),
  WEBHOOK_PROCESSOR_SQS_QUEUE_URL: z.string(),
  WESTHILL_UNIFIED_USERNAME: z.string(),
  WESTHILL_UNIFIED_PASSWORD: z.string(),
  WESTHILL_INTEGRATION_HOST: z.string(),
  WESTHILL_API_HOST: z.string(),
  // Optional full-URL override. Set by Portless to route through the proxy.
  WESTHILL_SERVER_URL: z.string().optional(),
  WESTHILL_SERVER_USERNAME: z.string(),
  WESTHILL_SERVER_PASSWORD: z.string(),
  WESTHILL_JOB_LEVEL_TARGET_DATES_DISABLED: z.coerce.boolean().optional().prefault(false),
  WESTHILL_PROCESSING_DISABLED: z.coerce.boolean().optional().prefault(false),
  WESTHILL_STATUS_UPDATES_ENABLED: z.coerce.boolean().optional().prefault(false),
  WESTHILL_XA_FILE_UPLOAD_ENABLED: z.coerce.boolean().optional().prefault(false),
  WESTHILL_XA_DOCUMENT_CHECK_DISABLED: z.stringbool().default(false),
  WESTHILL_XA_AUTOMATIONS_DISABLED: z.stringbool().default(true),
  WESTHILL_XA_NOTE_EMAIL_INGEST_ENABLED: z.stringbool().default(false),
  WESTHILL_XA_NOTE_EMAIL_MAILBOX: z.email().optional(),
  WESTHILL_VIBE_CHECK_UNIFIED_ASSIGNMENTS_SYNC_STALENESS_MINUTES: z.coerce.number().optional().prefault(90),
  WESTHILL_VIBE_CHECK_UNIFIED_NOTES_LOOKBACK_MINUTES: z.coerce.number().optional().prefault(180),
  WESTHILL_VIBE_CHECK_XA_ASSIGNMENTS_LOOKBACK_MINUTES: z.coerce.number().optional().prefault(120),
  WESTHILL_VIBE_CHECK_XA_NOTES_LOOKBACK_MINUTES: z.coerce.number().optional().prefault(120),
  WESTHILL_XA_TAB_COUNT: z.coerce.number().int().min(1).max(10).optional().prefault(2),
  PARTNER_API_HOST: z.string(),
  PARTNER_API_LOAD_BALANCER_HOST: z.string(),
  PARTNER_API_PORT: z.string(),
  // Optional full-URL override. Set by Portless to route through the proxy.
  // When present, the partner-integration service prefers this over HOST+PORT.
  PARTNER_API_URL: z.string().optional(),
  SVIX_AUTH_TOKEN: z.string(),
  AMERICAN_FAMILY_NOTIFICATION_EMAIL: z.string(),
  LANGSMITH_API_KEY: z.string(),
  LANGGRAPH_API_URL: z.url().optional(),
  LANGGRAPH_ASSISTANT_ID: z.string().optional(),
  REMI_FIRE_ALARM_USER_EMAIL: z.string(),
  REMI_FIRE_ALARM_USER_PASSWORD: z.string(),
  SKIP_PORTAL_CHECK: z.enum(["true", "false"]).catch("false"),

  WORKSITE_VIEW_SUMMARY_REAL_GENERATION_ENABLED: z.enum(["true", "false"]).optional(),

  SITE_DISCOVERY_SUMMARY_REAL_GENERATION_ENABLED: z.enum(["true", "false"]).optional(),

  // Evaluated per environment, not per actor: a per-actor flag cannot enforce a per-project invariant,
  // and queue/scheduler writes carry no user so would always evaluate false.
  WORK_ORDER_LOCK_ENFORCEMENT_ENABLED: z.enum(["true", "false"]).optional(),

  // Sentry configuration
  SENTRY_ORG: z.string().optional(),
  SENTRY_PROJECT: z.string().optional(),
  SENTRY_AUTH_TOKEN: z.string().optional(),
  SENTRY_DSN: z.string(),
  SENTRY_DSN_CREATE_THUMBNAIL: z.string(),
  SENTRY_PROJECT_REMI_APP: z.string(),
  SENTRY_PROJECT_PORTAL: z.string(),
  SENTRY_API_TOKEN: z.string(),
  SENTRY_DSN_ODDJOB_API: z.string(),
  SENTRY_DSN_WESTHILL_SERVER: z.string(),

  RDCL_API_HOST: z.string(),
  RDCL_API_USERNAME: z.string(),
  RDCL_API_PASSWORD: z.string(),
  RDCL_REMI_ORGANIZATION_ID: z.string(),
  RDCL_SITE_ASSESSMENT_TEMPLATE_ID: z.string(),
  RDCL_DAMAGE_ASSESSMENT_TEMPLATE_ID: z.string(),
  RDCL_ROOF_ASSESSMENT_TEMPLATE_ID: z.string(),
  RDCL_WEBHOOK_SECRET: z.string(),
  RDCL_ORG_API_KEY: z.string(),
  RDCL_SERVER_API_KEY: z.string(),
  RDCL_SERVER_BASE_URL: z.url(),
  RDCL_REINSPECTION_DETECTION_ENABLED: z.coerce.boolean().optional().prefault(false),
  // Unset binds nothing rather than everything: reinspections raised before this instant have no
  // PENDING_REINSPECTION hold to resolve against, so each would fail and retry on every sync.
  RDCL_REINSPECTION_CUTOFF_AT: z.coerce.date().optional(),
  // Optional rather than required: until a real RDCL address is configured, the reinspection dispatch
  // captures a no-op. A placeholder would satisfy `z.string()` and risk being emailed.
  RDCL_REINSPECTION_NOTIFICATION_EMAIL: z.string().optional(),

  ENSITE_API_HOST: z.string(),
  ENSITE_API_USERNAME: z.string(),
  ENSITE_API_PASSWORD: z.string(),
  ENSITE_CREATOR_PARENT_ID: z.coerce.number(),
  ENSITE_CLIENT_ID: z.coerce.number(),
  ENSITE_WEBHOOK_SECRET: z.string(),
  ENSITE_AUTOMATIONS_API_KEY: z.string(),
  ENSITE_SERVER_API_KEY: z.string(),
  ENSITE_SERVER_BASE_URL: z.url(),
  ENSITE_AUTOMATIONS_TOKEN_ID: z.string(),
  ENSITE_AUTOMATIONS_TOKEN: z.string(),
  ENSITE_AUTOMATIONS_SALT: z.string(),
  ENSITE_JOB_TYPE_SUNRUN_POST_PTO: z.string(),
  ENSITE_JOB_TYPE_INSURANCE: z.string(),
  ENSITE_JOB_TYPE_DEFAULT: z.string(),

  // AmFam
  AMFAM_REMI_INGESTION_WEBHOOK_SECRET: z.string(),
  AMFAM_AUTOMATIONS_API_KEY: z.string(),
  REMI_AUTOMATIONS_XA_AUTHOR: z.string().optional(),
  AMFAM_AUTOMATIONS_SALT: z.string(),
  AMFAM_AUTOMATIONS_TOKEN: z.string(),
  AMFAM_AUTOMATIONS_TOKEN_ID: z.string(),
  AMFAM_OUTBOUND_STATUS_ENABLED: z.string().optional().default("false"),
  AMFAM_OUTBOUND_FILES_ENABLED: z.string().optional().default("false"),

  XA_FILE_DELIVERY_MESSAGE_WESTHILL_ENABLED: z.string().optional().default("true"),
  XA_FILE_DELIVERY_MESSAGE_AMFAM_ENABLED: z.string().optional().default("true"),
  // zod's coerce.boolean() is Boolean(input): the string "false" parses as true, leaving this kill switch permanently on.
  WESTHILL_COMPLETION_SUMMARY_NOTE_ENABLED: z.string().optional().default("false"),
  WESTHILL_COMPLETION_FOLLOWUP_ENABLED: z.string().optional().default("false"),

  GITHUB_API_TOKEN: z.string(),
  GITHUB_ANALYTICS_WEBHOOK_SECRET: z.string(),

  // Linear merge-sync CI script (packages/linear/src/scripts/sync-merged-prs-to-linear.ts)
  GITHUB_REPOSITORY: z.string(),
  BEFORE_SHA: z.string(),
  AFTER_SHA: z.string(),

  // OTel Collector
  OTEL_COLLECTOR_ENDPOINT: z.string(),
  OTEL_COLLECTOR_AUTH_TOKEN: z.string(),

  // Oddjob API
  ODDJOB_API_HOST: z.string(),
  ODDJOB_API_PORT: z.string(),
  ODDJOB_API_LOAD_BALANCER_HOST: z.string(),
  ODDJOB_API_USERNAME: z.string(),
  ODDJOB_API_PASSWORD: z.string(),
  // Optional full-URL override. Set by Portless to route through the proxy.
  // When present, the oddjob SDK prefers this over HOST+PORT assembly.
  ODDJOB_API_URL: z.string().optional(),

  // Hover
  HOVER_CLIENT_ID: z.string(),
  HOVER_CLIENT_SECRET: z.string(),
  HOVER_REDIRECT_URI: z.url(),
  HOVER_BASE_URL: z.url(),
  HOVER_WEBHOOK_SECRET: z.string(),

  // Bouncie
  BOUNCIE_OAUTH_CLIENT_ID: z.string(),
  BOUNCIE_OAUTH_CLIENT_SECRET: z.string(),
  BOUNCIE_OAUTH_REDIRECT_URL: z.url(),
  BOUNCIE_WEBHOOK_KEY: z.string(),

  // Symbility
  SYMBILITY_API_BASE_URL: z.url(),
  SYMBILITY_CLIENT_ID: z.string(),
  SYMBILITY_CLIENT_SECRET: z.string(),

  // Openly
  OPENLY_SQS_QUEUE_URL: z.string(),
  OPENLY_AUTOMATIONS_API_KEY: z.string(),
  OPENLY_AUTOMATIONS_SALT: z.string(),
  OPENLY_AUTOMATIONS_TOKEN: z.string(),
  OPENLY_AUTOMATIONS_TOKEN_ID: z.string(),
  OPENLY_API_USERNAME: z.string(),
  OPENLY_API_PASSWORD: z.string(),
  OPENLY_PDF_PARSER_URL: z.string(),
  OPENLY_WEBHOOK_URL: z.string().optional(),
  OPENLY_COMPANY_ID: z.string(),
  OPENLY_MESSAGE_INGESTION_WEBHOOK_SECRET: z.string(),
  SYMBILITY_SERVICE_ACCOUNT_USER_ID: z.string(),

  // Inngest (Vercel integration prefixes with REMI_INNGEST_)
  INNGEST_EVENT_KEY: z.string().optional(),
  INNGEST_SIGNING_KEY: z.string().optional(),
  REMI_INNGEST_INNGEST_EVENT_KEY: z.string().optional(),
  REMI_INNGEST_INNGEST_SIGNING_KEY: z.string().optional(),
  INNGEST_BASE_URL: z.url().optional(),
} as const satisfies Record<string, z.Schema> & Record<`NEXT_PUBLIC_${string}`, never>;

export type RemiEnvVar = keyof typeof RemiEnvSchema;

const RemiPublicEnvSchema = {
  NEXT_PUBLIC_MAPBOX_TOKEN: z.string(),
  NEXT_PUBLIC_APP_URL: z.url(),
  NEXT_PUBLIC_EDEN_URL: z.url().optional(),
  NEXT_PUBLIC_ANTHROPIC_API_KEY: z.string(),
  NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: z.string(),
  NEXT_PUBLIC_GOOGLE_PLACES_API_KEY: z.string(),
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: z.string(),
  NEXT_PUBLIC_KNOCK_FEED_ID: z.string(),
  NEXT_PUBLIC_KNOCK_PUBLIC_API_KEY: z.string(),
  NEXT_PUBLIC_PAY_KEEPER_BASE_URL: z.string(),
  NEXT_PUBLIC_PORTAL_URL: z.url(),
  NEXT_PUBLIC_POSTHOG_API_KEY: z.string(),
  NEXT_PUBLIC_POSTHOG_HOST: z.string(),
  NEXT_PUBLIC_SERVER_URL: z.url(),
  NEXT_PUBLIC_USE_EMULATOR: z.string().optional(),
  NEXT_PUBLIC_VERCEL_ENV: EnvironmentSchema.optional(),
  EXPO_PUBLIC_API_URL: z.url(),
  EXPO_PUBLIC_MAPBOX_TOKEN: z.string().optional(),
  // Sentry public configuration
  NEXT_PUBLIC_SENTRY_DSN: z.string(),
  NEXT_PUBLIC_SENTRY_DSN_PORTAL: z.string(),
  NEXT_PUBLIC_SENTRY_DSN_REMI_APP: z.string(),
  EXPO_PUBLIC_SENTRY_DSN: z.string(),
  NEXT_PUBLIC_LANGSMITH_PROJECT: z.string(),
  NEXT_PUBLIC_SENTRY_DSN_PLAYGROUND: z.string(),
  NEXT_PUBLIC_FORCE_ENABLE_SENTRY: z.coerce.boolean().optional(),
  EXPO_PUBLIC_FORCE_ENABLE_SENTRY: z.coerce.boolean().optional(),
  NEXT_PUBLIC_DROPBOX_SIGN_CLIENT_ID: z.string(),
  NEXT_PUBLIC_POSTHOG_PREVIEW_ENABLED: z.coerce.boolean().optional(),
  NEXT_PUBLIC_SHINGLE_IMAGES_BASE_URL: z.url().optional(),
  NEXT_PUBLIC_WEB_PUSH_VAPID_PUBLIC_KEY: z.string().optional(),
  NEXT_PUBLIC_TRAINING_SANDBOX: z.enum(["true", "false"]).optional(),
  NEXT_PUBLIC_WESTHILL_XA_AUTOMATIONS_ENABLED: z.stringbool().default(true),
} as const satisfies Record<`${"NEXT_PUBLIC_" | "EXPO_PUBLIC_"}${string}`, z.Schema>;

/** This is necessary to get Next.js to expose the variables from build step to the client.
 *
 * See more here: https://nextjs.org/docs/pages/building-your-application/configuring/environment-variables
 **/
const publicEnvVars = {
  NEXT_PUBLIC_MAPBOX_TOKEN: process.env.NEXT_PUBLIC_MAPBOX_TOKEN,
  NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
  NEXT_PUBLIC_EDEN_URL: process.env.NEXT_PUBLIC_EDEN_URL,
  NEXT_PUBLIC_ANTHROPIC_API_KEY: process.env.NEXT_PUBLIC_ANTHROPIC_API_KEY,
  NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
  NEXT_PUBLIC_GOOGLE_PLACES_API_KEY: process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY,
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
  NEXT_PUBLIC_KNOCK_FEED_ID: process.env.NEXT_PUBLIC_KNOCK_FEED_ID,
  NEXT_PUBLIC_KNOCK_PUBLIC_API_KEY: process.env.NEXT_PUBLIC_KNOCK_PUBLIC_API_KEY,
  NEXT_PUBLIC_PAY_KEEPER_BASE_URL: process.env.NEXT_PUBLIC_PAY_KEEPER_BASE_URL,
  NEXT_PUBLIC_PORTAL_URL: process.env.NEXT_PUBLIC_PORTAL_URL,
  NEXT_PUBLIC_POSTHOG_API_KEY: process.env.NEXT_PUBLIC_POSTHOG_API_KEY,
  NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
  NEXT_PUBLIC_SERVER_URL: process.env.NEXT_PUBLIC_SERVER_URL,
  NEXT_PUBLIC_USE_EMULATOR: process.env.NEXT_PUBLIC_USE_EMULATOR,
  NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
  EXPO_PUBLIC_API_URL: process.env.EXPO_PUBLIC_API_URL,
  EXPO_PUBLIC_MAPBOX_TOKEN: process.env.EXPO_PUBLIC_MAPBOX_TOKEN,
  NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
  NEXT_PUBLIC_SENTRY_DSN_PORTAL: process.env.NEXT_PUBLIC_SENTRY_DSN_PORTAL,
  NEXT_PUBLIC_SENTRY_DSN_REMI_APP: process.env.NEXT_PUBLIC_SENTRY_DSN_REMI_APP,
  NEXT_PUBLIC_SENTRY_DSN_PLAYGROUND: process.env.NEXT_PUBLIC_SENTRY_DSN_PLAYGROUND,
  EXPO_PUBLIC_SENTRY_DSN: process.env.EXPO_PUBLIC_SENTRY_DSN,
  NEXT_PUBLIC_LANGSMITH_PROJECT: process.env.NEXT_PUBLIC_LANGSMITH_PROJECT,
  NEXT_PUBLIC_FORCE_ENABLE_SENTRY: process.env.NEXT_PUBLIC_FORCE_ENABLE_SENTRY,
  EXPO_PUBLIC_FORCE_ENABLE_SENTRY: process.env.EXPO_PUBLIC_FORCE_ENABLE_SENTRY,
  NEXT_PUBLIC_DROPBOX_SIGN_CLIENT_ID: process.env.NEXT_PUBLIC_DROPBOX_SIGN_CLIENT_ID,
  NEXT_PUBLIC_POSTHOG_PREVIEW_ENABLED: process.env.NEXT_PUBLIC_POSTHOG_PREVIEW_ENABLED,
  NEXT_PUBLIC_SHINGLE_IMAGES_BASE_URL: process.env.NEXT_PUBLIC_SHINGLE_IMAGES_BASE_URL,
  NEXT_PUBLIC_WEB_PUSH_VAPID_PUBLIC_KEY: process.env.NEXT_PUBLIC_WEB_PUSH_VAPID_PUBLIC_KEY,
  NEXT_PUBLIC_TRAINING_SANDBOX: process.env.NEXT_PUBLIC_TRAINING_SANDBOX,
  NEXT_PUBLIC_WESTHILL_XA_AUTOMATIONS_ENABLED: process.env.NEXT_PUBLIC_WESTHILL_XA_AUTOMATIONS_ENABLED,
} as const satisfies Record<keyof typeof RemiPublicEnvSchema, string | undefined>;

type EnvType<T extends RemiEnvVar> = z.infer<(typeof RemiEnvSchema)[T]>;
type PublicEnvType<T extends keyof typeof RemiPublicEnvSchema> = z.infer<(typeof RemiPublicEnvSchema)[T]>;

/**
 * Typesafe way to get an env variable at runtime
 * Only throws when attempting to access, not during build
 */
export const getEnv = <TKey extends RemiEnvVar>(name: TKey): EnvType<TKey> => {
  if (typeof window !== "undefined") {
    throw new RemiError({
      remiPackage: RemiPackage.ENVIRONMENT,
      errorType: "NON_PUBLIC_ENV_ACCESS_ON_CLIENT",
      severity: ErrorSeverity.URGENT,
      data: {
        variableName: name,
      },
    });
  }
  const schema = RemiEnvSchema[name];
  const value = process.env[name];
  // oxlint-disable-next-line remi/no-type-assertions -- Zod v4 generic inference resolves to unknown — schema validates the correct type
  return parseEnv(schema, value, name) as EnvType<TKey>;
};

export const getEnvPublic = <TKey extends keyof typeof RemiPublicEnvSchema>(name: TKey): PublicEnvType<TKey> => {
  const schema = RemiPublicEnvSchema[name];
  // oxlint-disable-next-line remi/no-type-assertions -- Zod v4 generic inference resolves to unknown — schema validates the correct type
  return parseEnv(schema, publicEnvVars[name], name) as PublicEnvType<TKey>;
};

export const TEST_PLACEHOLDER_URL = "http://localhost:3000";

const parseEnv = <T extends z.Schema>(
  schema: T,
  value: string | undefined,
  name: keyof typeof RemiEnvSchema | keyof typeof RemiPublicEnvSchema,
): z.infer<T> => {
  const result = schema.safeParse(value as unknown);
  if (!result.success) {
    // Return dummy secrets in unit tests to ensure isolation
    if (isCIEnvironment() || !!process.env.VITEST) {
      // Test-only fallback when a URL env var is missing under CI or Vitest. Never used
      // in local dev — APP_URL is set by Doppler, Portless overrides, or remi-env.sh.
      if (name.includes("URL")) {
        // oxlint-disable-next-line remi/no-type-assertions -- Zod v4 z.infer resolves to unknown — cast required
        return TEST_PLACEHOLDER_URL as z.infer<T>;
      }
      if (name === "ABLY_API_KEY") {
        // oxlint-disable-next-line remi/no-type-assertions -- Zod v4 z.infer resolves to unknown — cast required
        return "FAKE.FAKE:FAKE" as z.infer<T>;
      }
      // oxlint-disable-next-line remi/no-type-assertions -- Zod v4 z.infer resolves to unknown — cast required
      return "invalid" as z.infer<T>;
    }
    throw new RemiError({
      remiPackage: RemiPackage.ENVIRONMENT,
      errorType: "INVALID_ENV_VARIABLE",
      severity: ErrorSeverity.URGENT,
      additionalDescription: ` ${name} is invalid`,
      data: {
        variableName: name,
        errorMessage: result.error.message,
      },
    });
  }
  // oxlint-disable-next-line remi/no-type-assertions -- Zod v4 z.infer resolves to unknown — cast required
  return result.data as z.infer<T>;
};

export function isCIEnvironment(): boolean {
  if (typeof window !== "undefined") {
    return false;
  }
  return process.env.CI === "true" || process.env.CI === "1";
}
